import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { RefreshCw, Search, ShoppingBag, Eye, CheckCircle, Clock, XCircle, FileText, Pencil, Trash2, MessageSquare, Plus, User, Phone, Building } from 'lucide-react';
import { format } from 'date-fns';

interface CEOrder {
  id: string;
  created_at: string;
  package_id: string;
  package_name: string;
  amount_gbp: number;
  payment_status: string;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_customer_email: string | null;
  permission_confirmed: boolean;
  permission_confirmed_at: string | null;
  permission_confirmed_ip: string | null;
  permission_statement_text: string | null;
  onboarding?: CEOnboarding | null;
}

interface CEOnboarding {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  phone: string | null;
  primary_domain: string | null;
  staff_count: number;
  email_platform: string;
  device_mix: string;
  it_management: string;
  mfa_status: string;
  encryption_status: string;
  patching_status: string;
  submitted_at: string;
}

interface CEOrderNote {
  id: string;
  order_id: string;
  note_text: string;
  note_type: string;
  created_by: string | null;
  created_at: string;
}

export function CEOrdersManagement() {
  const { toast } = useToast();
  const [orders, setOrders] = useState<CEOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'paid' | 'pending' | 'failed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<CEOrder | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Edit state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editOrder, setEditOrder] = useState<CEOrder | null>(null);
  const [editForm, setEditForm] = useState({
    payment_status: '',
    amount_gbp: 0,
    stripe_customer_email: '',
    package_name: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete state
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteOrder, setDeleteOrder] = useState<CEOrder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<CEOrderNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'internal' | 'customer_communication' | 'system'>('internal');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [orderNoteCounts, setOrderNoteCounts] = useState<Record<string, number>>({});

  const loadOrders = async () => {
    setLoading(true);
    try {
      // Fetch orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('ce_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Fetch onboarding data
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('ce_onboarding')
        .select('*');

      if (onboardingError) throw onboardingError;

      // Fetch note counts for all orders
      const { data: noteCountsData, error: noteCountsError } = await supabase
        .from('ce_order_notes')
        .select('order_id');

      if (noteCountsError) throw noteCountsError;

      // Count notes per order
      const noteCounts: Record<string, number> = {};
      (noteCountsData || []).forEach(note => {
        noteCounts[note.order_id] = (noteCounts[note.order_id] || 0) + 1;
      });
      setOrderNoteCounts(noteCounts);

      // Join orders with onboarding
      const ordersWithOnboarding = (ordersData || []).map(order => ({
        ...order,
        onboarding: onboardingData?.find(ob => ob.order_id === order.id) || null
      }));

      setOrders(ordersWithOnboarding);
    } catch (error: any) {
      toast({
        title: 'Error loading orders',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async (orderId: string) => {
    setNotesLoading(true);
    try {
      const { data, error } = await supabase
        .from('ce_order_notes')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading notes',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedOrder || !newNote.trim()) return;
    
    setIsAddingNote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('ce_order_notes')
        .insert({
          order_id: selectedOrder.id,
          note_text: newNote.trim(),
          note_type: noteType,
          created_by: user?.id || null,
        });

      if (error) throw error;

      toast({
        title: 'Note added',
        description: 'The note has been added successfully.',
      });
      setNewNote('');
      loadNotes(selectedOrder.id);
      // Update note count
      setOrderNoteCounts(prev => ({
        ...prev,
        [selectedOrder.id]: (prev[selectedOrder.id] || 0) + 1
      }));
    } catch (error: any) {
      toast({
        title: 'Error adding note',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!selectedOrder) return;
    
    try {
      const { error } = await supabase
        .from('ce_order_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      toast({
        title: 'Note deleted',
        description: 'The note has been deleted.',
      });
      loadNotes(selectedOrder.id);
      // Update note count
      setOrderNoteCounts(prev => ({
        ...prev,
        [selectedOrder.id]: Math.max((prev[selectedOrder.id] || 1) - 1, 0)
      }));
    } catch (error: any) {
      toast({
        title: 'Error deleting note',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === 'all' || order.payment_status === filter;
    const matchesSearch = !searchTerm || 
      order.stripe_customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.package_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      paid: 'default',
      pending: 'secondary',
      failed: 'destructive',
    };
    return (
      <Badge variant={variants[status] || 'outline'} className="gap-1">
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Amount is stored in pence, display in pounds
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount / 100);
  };

  const stats = {
    total: orders.length,
    paid: orders.filter(o => o.payment_status === 'paid').length,
    pending: orders.filter(o => o.payment_status === 'pending').length,
    revenue: orders.filter(o => o.payment_status === 'paid').reduce((sum, o) => sum + o.amount_gbp, 0),
  };

  const handleViewDetails = (order: CEOrder) => {
    setSelectedOrder(order);
    setNotes([]);
    setNewNote('');
    setNoteType('internal');
    loadNotes(order.id);
    setIsDetailsOpen(true);
  };

  const getNoteTypeBadge = (type: string) => {
    switch (type) {
      case 'internal':
        return <Badge variant="secondary">Internal</Badge>;
      case 'customer_communication':
        return <Badge variant="default" className="bg-blue-600">Customer</Badge>;
      case 'system':
        return <Badge variant="outline">System</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const handleEditClick = (order: CEOrder) => {
    setEditOrder(order);
    setEditForm({
      payment_status: order.payment_status,
      amount_gbp: order.amount_gbp / 100, // Convert pence to pounds for editing
      stripe_customer_email: order.stripe_customer_email || '',
      package_name: order.package_name,
    });
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editOrder) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('ce_orders')
        .update({
          payment_status: editForm.payment_status,
          amount_gbp: Math.round(editForm.amount_gbp * 100), // Convert pounds to pence
          stripe_customer_email: editForm.stripe_customer_email || null,
          package_name: editForm.package_name,
        })
        .eq('id', editOrder.id);

      if (error) throw error;

      toast({
        title: 'Order updated',
        description: 'The order has been updated successfully.',
      });
      setIsEditOpen(false);
      loadOrders();
    } catch (error: any) {
      toast({
        title: 'Error updating order',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (order: CEOrder) => {
    setDeleteOrder(order);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteOrder) return;
    
    setIsDeleting(true);
    try {
      // First delete associated onboarding if exists
      if (deleteOrder.onboarding) {
        const { error: onboardingError } = await supabase
          .from('ce_onboarding')
          .delete()
          .eq('order_id', deleteOrder.id);
        
        if (onboardingError) throw onboardingError;
      }

      // Then delete the order
      const { error } = await supabase
        .from('ce_orders')
        .delete()
        .eq('id', deleteOrder.id);

      if (error) throw error;

      toast({
        title: 'Order deleted',
        description: 'The order and associated data have been deleted.',
      });
      setIsDeleteOpen(false);
      loadOrders();
    } catch (error: any) {
      toast({
        title: 'Error deleting order',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                CE Orders
              </CardTitle>
              <CardDescription>
                Manage Cyber Essentials package orders and onboarding
              </CardDescription>
            </div>
            <Button variant="outline" onClick={loadOrders} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Orders</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Paid</p>
              <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Revenue</p>
              <p className="text-2xl font-bold">{formatAmount(stats.revenue)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="failed">Failed</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or package..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Onboarding</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      {searchTerm || filter !== 'all' 
                        ? 'No orders match your filters' 
                        : 'No orders yet'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-xs">
                        {order.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="font-medium">
                        {order.package_name}
                      </TableCell>
                      <TableCell>
                        {order.stripe_customer_email || '-'}
                      </TableCell>
                      <TableCell>
                        {formatAmount(order.amount_gbp)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(order.payment_status)}
                      </TableCell>
                      <TableCell>
                        {order.onboarding ? (
                          <Badge variant="default" className="gap-1">
                            <FileText className="w-3 h-3" />
                            Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1">
                            <Clock className="w-3 h-3" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {orderNoteCounts[order.id] ? (
                          <Badge variant="secondary" className="gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {orderNoteCounts[order.id]}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(order)}
                            title="Edit order"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(order)}
                            title="Delete order"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Order Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Order ID</p>
                    <p className="font-mono">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p>{format(new Date(selectedOrder.created_at), 'PPpp')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Package</p>
                    <p className="font-medium">{selectedOrder.package_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Amount</p>
                    <p className="font-medium">{formatAmount(selectedOrder.amount_gbp)}</p>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Payment Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    {getStatusBadge(selectedOrder.payment_status)}
                  </div>
                  <div>
                    <p className="text-muted-foreground">Customer Email</p>
                    <p>{selectedOrder.stripe_customer_email || '-'}</p>
                  </div>
                  {selectedOrder.stripe_session_id && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Stripe Session ID</p>
                      <p className="font-mono text-xs break-all">{selectedOrder.stripe_session_id}</p>
                    </div>
                  )}
                  {selectedOrder.stripe_payment_intent_id && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Payment Intent ID</p>
                      <p className="font-mono text-xs break-all">{selectedOrder.stripe_payment_intent_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Permission Confirmation */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Permission Confirmation</h3>
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground">Confirmed:</p>
                    {selectedOrder.permission_confirmed ? (
                      <Badge variant="default">Yes</Badge>
                    ) : (
                      <Badge variant="secondary">No</Badge>
                    )}
                  </div>
                  {selectedOrder.permission_confirmed_at && (
                    <div>
                      <p className="text-muted-foreground">Confirmed At</p>
                      <p>{format(new Date(selectedOrder.permission_confirmed_at), 'PPpp')}</p>
                    </div>
                  )}
                  {selectedOrder.permission_confirmed_ip && (
                    <div>
                      <p className="text-muted-foreground">IP Address</p>
                      <p className="font-mono">{selectedOrder.permission_confirmed_ip}</p>
                    </div>
                  )}
                  {selectedOrder.permission_statement_text && (
                    <div>
                      <p className="text-muted-foreground">Statement Text</p>
                      <p className="text-xs bg-secondary/50 p-2 rounded">{selectedOrder.permission_statement_text}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Onboarding Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Onboarding Information</h3>
                {selectedOrder.onboarding ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Company Name</p>
                      <p className="font-medium">{selectedOrder.onboarding.company_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Contact Name</p>
                      <p>{selectedOrder.onboarding.contact_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Contact Email</p>
                      <p>{selectedOrder.onboarding.contact_email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p>{selectedOrder.onboarding.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Primary Domain</p>
                      <p>{selectedOrder.onboarding.primary_domain || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Staff Count</p>
                      <p>{selectedOrder.onboarding.staff_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email Platform</p>
                      <p>{selectedOrder.onboarding.email_platform}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Device Mix</p>
                      <p>{selectedOrder.onboarding.device_mix}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">IT Management</p>
                      <p>{selectedOrder.onboarding.it_management}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">MFA Status</p>
                      <p>{selectedOrder.onboarding.mfa_status}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Encryption Status</p>
                      <p>{selectedOrder.onboarding.encryption_status}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Patching Status</p>
                      <p>{selectedOrder.onboarding.patching_status}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Submitted At</p>
                      <p>{format(new Date(selectedOrder.onboarding.submitted_at), 'PPpp')}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Onboarding form not yet submitted
                  </p>
                )}
              </div>

              <Separator />

              {/* Order Notes Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Order Notes
                  </h3>
                  <Badge variant="secondary">{notes.length} notes</Badge>
                </div>

                {/* Add Note Form */}
                <div className="space-y-3 p-4 bg-secondary/30 rounded-lg">
                  <div className="flex gap-2">
                    <Select
                      value={noteType}
                      onValueChange={(value: 'internal' | 'customer_communication' | 'system') => setNoteType(value)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal Note</SelectItem>
                        <SelectItem value="customer_communication">Customer Communication</SelectItem>
                        <SelectItem value="system">System Note</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    placeholder="Add a note about this order..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="min-h-[80px]"
                  />
                  <Button 
                    onClick={handleAddNote} 
                    disabled={isAddingNote || !newNote.trim()}
                    className="w-full sm:w-auto"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {isAddingNote ? 'Adding...' : 'Add Note'}
                  </Button>
                </div>

                {/* Notes List */}
                {notesLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className="p-3 border rounded-lg">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-16 w-full" />
                      </div>
                    ))}
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No notes yet. Add your first note above.
                  </p>
                ) : (
                  <ScrollArea className="max-h-[300px]">
                    <div className="space-y-3 pr-4">
                      {notes.map((note) => (
                        <div key={note.id} className="p-3 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getNoteTypeBadge(note.note_type)}
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNote(note.id)}
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
            <DialogDescription>
              Update order details. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-package">Package Name</Label>
              <Input
                id="edit-package"
                value={editForm.package_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, package_name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email">Customer Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.stripe_customer_email}
                onChange={(e) => setEditForm(prev => ({ ...prev, stripe_customer_email: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-amount">Amount (£)</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                min="0"
                value={editForm.amount_gbp}
                onChange={(e) => setEditForm(prev => ({ ...prev, amount_gbp: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-status">Payment Status</Label>
              <Select
                value={editForm.payment_status}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, payment_status: value }))}
              >
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this order?
              {deleteOrder?.onboarding && (
                <span className="block mt-2 text-destructive">
                  This order has associated onboarding data which will also be deleted.
                </span>
              )}
              <span className="block mt-2">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
